/**
 * components/landing/home/TradingAcademy.jsx — Section 8.
 *
 * A realistic learning platform preview:
 *   • Left: current course + learning path.
 *   • Right: modules list with progress bars, an assessment card, and a
 *     recent certificate.
 */

import { Container, SectionHeading, Eyebrow, GradientButton, GhostButton } from '../design-system'
import { MockDashboardChrome } from './_shared'
import { GraduationCap, Award, BookOpen, Users, PlayCircle, CheckCircle2, ArrowRight } from 'lucide-react'
import clsx from 'clsx'

const LESSONS = [
  { title: 'Market structure fundamentals',    len: '18 min', done: true  },
  { title: 'Order blocks & liquidity',         len: '24 min', done: true  },
  { title: 'Multi-timeframe alignment',        len: '32 min', done: true  },
  { title: 'Entry mechanics + risk',           len: '28 min', done: false, active: true },
  { title: 'Trade management + exits',         len: '22 min', done: false },
  { title: 'Backtesting your first strategy',  len: '35 min', done: false },
]

const COURSES = [
  { title: 'SMC Foundations',     progress: 62, level: 'Intermediate' },
  { title: 'Risk & Position Sizing', progress: 100, level: 'Beginner' },
  { title: 'Options 101',         progress: 15, level: 'Beginner' },
  { title: 'AI-Assisted Trading', progress: 34, level: 'Advanced' },
]

const TradingAcademy = () => (
  <section className="py-24 md:py-40">
    <Container>
      <SectionHeading
        eyebrow="Trading Academy"
        title={<>Built-in learning that <span className="text-brand-gold">grows tenants into traders</span>.</>}
        subtitle="Every Kalki Ai Cloud tenant ships with a full learning platform — courses, adaptive paths, assessments, certificates and community."
        align="center"
        className="items-center mx-auto"
      />

      <div className="mt-14 max-w-6xl mx-auto">
        <MockDashboardChrome innerClass="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[440px]">
          {/* Current course */}
          <div className="md:col-span-2 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl border border-brand-gold/30 bg-brand-gold/10 text-brand-gold flex items-center justify-center"><GraduationCap className="h-5 w-5" /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Continue learning</div>
                  <div className="text-white font-semibold">SMC Foundations</div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono">3 / 6 lessons</div>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-brand-gold to-[#FFE17A]" style={{ width: '62%' }} />
            </div>
            <div className="mt-4 space-y-1.5">
              {LESSONS.map((l) => (
                <div key={l.title} className={clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                  l.active ? 'bg-brand-gold/[0.08] border border-brand-gold/25' : 'bg-white/[0.02] border border-white/5',
                )}>
                  {l.done ? <CheckCircle2 className="h-4 w-4 text-brand-emerald" />
                          : l.active ? <PlayCircle className="h-4 w-4 text-brand-gold" />
                                     : <div className="h-4 w-4 rounded-full border border-white/15" />}
                  <div className="flex-1 text-[13px] text-white">{l.title}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{l.len}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — assessment + certificate + community */}
          <div className="space-y-3">
            <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-4 w-4 text-brand-gold" />
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Assessment</div>
              </div>
              <div className="text-white font-semibold text-sm">Module 3 quiz</div>
              <div className="text-[11px] text-zinc-400 mt-1">12 questions · 20 min</div>
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-brand-gold">
                Start assessment <ArrowRight className="h-3 w-3" />
              </div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-brand-emerald" />
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Recent certificate</div>
              </div>
              <div className="text-white font-semibold text-sm">Risk & Position Sizing</div>
              <div className="text-[11px] text-zinc-400 mt-1">Awarded 3 days ago · Grade A</div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-brand-gold" />
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Community</div>
              </div>
              <div className="text-[11px] text-zinc-400 leading-relaxed">4 new discussions today in SMC Foundations. Post your trade for peer review.</div>
            </div>
          </div>

          {/* Learning path (all courses) — full width */}
          <div className="md:col-span-3 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Learning path</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {COURSES.map((c) => (
                <div key={c.title} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{c.level}</div>
                  <div className="text-white text-sm font-semibold mt-1">{c.title}</div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className={clsx('h-full', c.progress === 100 ? 'bg-brand-emerald' : 'bg-gradient-to-r from-brand-gold to-[#FFE17A]')} style={{ width: `${c.progress}%` }} />
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-1">{c.progress}%</div>
                </div>
              ))}
            </div>
          </div>
        </MockDashboardChrome>

        <div className="mt-8 flex items-center justify-center gap-3">
          <GradientButton href="/platform#academy">Explore Academy</GradientButton>
          <GhostButton href="/contact">Schedule demo</GhostButton>
        </div>
      </div>
    </Container>
  </section>
)

export default TradingAcademy
